import NiceModal from "@ebay/nice-modal-react";
import clsx from "clsx";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";

import { ReactComponent as ConnectIcon } from "assets/icons/connect.svg";
import Board from "components/Board";
import Box from "components/Box";
import Empty from "components/Empty";
import DeactivationViewModal from "components/modals/DeactivationViewModal";
import ReportDetailModal from "components/modals/ReportDetailModal";
import Pagination from "components/Pagination";
import EnhancedSearchField from "components/SearchField";
import Select from "components/Select";
import SortButton from "components/SortButton";
import Table from "components/Table";
import TitleHeading from "components/TitleHeading";
import { useGetGrades } from "hooks/apis/grade";
import {
  useGetMonitoringUsers,
  useGetMonitoringUsersCount,
} from "hooks/apis/user";
import useHandleParams from "hooks/useHandleParams";
import format from "lib/format";
import { connectionStatusOptions, linkingStatusOptions } from "utils/const";
import { isFiveMinutesAgo } from "utils/utils";

type FormValues = {
  page: number;
  connectionStatus: number | "all";
  grade: number | "all";
  linkingStatus: string;
  searchText: string;
  sort?: string;
};

const size = 20;

const MonitoringAssistantPage = () => {
  const { methods, searchRef } = useHandleParams<FormValues>({
    defaultValues: {
      page: 1,
      connectionStatus: "all",
      grade: "all",
      linkingStatus: "all",
      searchText: "",
    },
  });

  const form = methods.watch();

  const { data, totalItems, currentPage, isFetching } = useGetMonitoringUsers({
    size,
    ...form,
  });
  const { data: counts } = useGetMonitoringUsersCount();
  const { data: grades } = useGetGrades();

  const gradeOptions = useMemo(
    () => [
      { label: "전체 등급", value: "all" },
      ...(grades || []).map((grade) => ({
        label: grade.name,
        value: grade.id,
      })),
    ],
    [grades],
  );

  const onViewReport = ({
    id,
    name,
    userId,
  }: {
    id: number;
    name: string;
    userId: string;
  }) => {
    NiceModal.show(ReportDetailModal, { id, name, userId });
  };

  const onViewDeactivation = (id: number) => {
    NiceModal.show(DeactivationViewModal, { id });
  };

  return (
    <FormProvider {...methods}>
      <main className="p-[1.875rem] pl-[15rem] 1350:pl-[16rem] 1400:pl-[16.875rem] w-full">
        <TitleHeading
          className="mb-[0.625rem]"
          title="조교"
          description="질문 및 답변 현황 화면입니다."
        />
        <div className="flex gap-[0.625rem]">
          <div className="flex-[3]">
            <Board
              title="조교 접속 현황"
              rightLabel={`조회 시간 : ${format(counts?.date, "yyyy-MM-dd HH:mm:ss")}`}
              values={[
                {
                  label: "총 조교 수",
                  value: `${counts?.totalUsers ?? 0}`,
                  unit: "명",
                  className: "text-black",
                },
                {
                  label: "접속중인 조교",
                  value: `${counts?.connectedUsers ?? 0}`,
                  unit: "명",
                  className: "text-primary",
                },
                {
                  label: "미접속 조교",
                  value: `${counts ? counts.totalUsers - counts.connectedUsers : 0}`,
                  unit: "명",
                },
              ]}
            ></Board>
          </div>
          <div className="flex-[2]">
            <Board
              title="아이디 연동 현황"
              rightLabel={`조회 시간 : ${format(counts?.date, "yyyy-MM-dd HH:mm:ss")}`}
              values={[
                {
                  label: "정상 연동",
                  value: `${counts ? counts.totalUsers - counts.unlinkedUsers : 0}`,
                  unit: "건",
                  className: "text-black",
                },
                {
                  label: "비정상 연동",
                  value: `${counts?.unlinkedUsers ?? 0}`,
                  unit: "건",
                  className: "text-red",
                },
              ]}
            ></Board>
          </div>
        </div>
        <div className="flex gap-[0.625rem] my-[0.625rem]">
          <div className="flex-[3] flex gap-[0.625rem]">
            <Select
              name="connectionStatus"
              options={connectionStatusOptions}
              size="bigger"
              className="!w-[9.375rem]"
            />
            <Select
              name="grade"
              options={gradeOptions}
              size="bigger"
              className="!w-[9.375rem]"
            />
            <Select
              name="linkingStatus"
              options={linkingStatusOptions}
              size="bigger"
              className="!w-[9.375rem]"
            />
          </div>
          <div className="flex-[2]">
            <EnhancedSearchField ref={searchRef} />
          </div>
        </div>
        <Box className="!py-5 min-h-[calc(100%-23.563rem)] flex flex-col">
          <span className="text-16 font-medium text-secondary">
            총 {(totalItems ?? 0).toLocaleString()}개
          </span>
          <Table
            classNames={["", "", "", "", "", "min-w-[8rem]", "min-w-[8rem]"]}
            className="[&>thead>tr>th]:w-[calc(100%/9)] [&>tbody>tr>td]:w-[calc(100%/9)]"
          >
            <thead>
              <tr>
                <th>번호</th>
                <th>등급</th>
                <th>이름</th>
                <th>접속 상태</th>
                <th>연동 상태</th>
                <th>
                  <SortButton name="monthly" defaultValue="monthly">
                    월간 답변 수
                  </SortButton>
                </th>
                <th>
                  <SortButton name="daily" defaultValue="monthly">
                    일일 답변 수
                  </SortButton>
                </th>
                <th>이관 횟수</th>
                <th>신고 횟수</th>
                <th>누적 정지 수</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((user, index) => (
                <tr key={user.id}>
                  <td>{totalItems - index - size * (currentPage - 1)}</td>
                  <td>{user.grade.name}</td>
                  <td>
                    <div className="flex flex-col items-center">
                      <span>{user.name}</span>
                      <span className="text-12">
                        {user ? `(${user.userId})` : ""}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span
                      className={clsx(
                        "mx-auto text-14 text-white rounded-md w-[3.125rem] h-[1.625rem] inline-flex justify-center items-center",
                        isFiveMinutesAgo(user.lastSeen) ? "bg-green" : "bg-red",
                      )}
                    >
                      {isFiveMinutesAgo(user.lastSeen) ? "접속중" : "미접속"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={clsx(
                        "mx-auto size-[1.625rem] rounded-md inline-flex justify-center items-center",
                        user.isLinked ? "bg-green" : "bg-red",
                      )}
                    >
                      <ConnectIcon className="size-[1.125rem]" />
                    </span>
                  </td>
                  <td>{user.monthCount?.toLocaleString?.()}</td>
                  <td>{user.dayCount?.toLocaleString?.()}</td>
                  <td>{user.transferCount?.toLocaleString?.()}</td>
                  <td>
                    <button
                      type="button"
                      className="underline"
                      onClick={() =>
                        onViewReport({
                          id: user.id,
                          name: user.name,
                          userId: user.userId,
                        })
                      }
                    >
                      {(user.reportCount ?? 0).toLocaleString()}/3
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="underline"
                      onClick={() => onViewDeactivation(user.id)}
                    >
                      {(user.deactivationCount ?? 0).toLocaleString()}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Empty show={!isFetching && data && !data.length} />
          <Pagination total={totalItems} />
        </Box>
      </main>
    </FormProvider>
  );
};

export default MonitoringAssistantPage;
